import React from 'react';
import {
  ExternalLink,
  Clock,
  ShieldCheck,
  Zap,
  Info,
  CheckCircle2,
  Building2,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import { CanonicalTransferQuote } from '../../types';
import { financialFormat, formatRateDisplay } from '../../services/fxEngine';
import { transferEngine } from '../../services/transferEngine';

interface TransferProviderCardProps {
  quote: CanonicalTransferQuote;
  rankIndex: number;
  onOpenDetails: (quote: CanonicalTransferQuote) => void;
}

export const TransferProviderCard: React.FC<TransferProviderCardProps> = ({
  quote,
  rankIndex,
  onOpenDetails,
}) => {
  const isBest = rankIndex === 0;

  return (
    <div
      className={`bg-white border rounded-2xl p-5 transition-all duration-200 hover:shadow-md ${
        isBest
          ? 'border-[#0E7044] ring-2 ring-[#0E7044]/10 bg-linear-to-b from-[#F0FDF4]/30 to-white'
          : 'border-[#E5E7EB] hover:border-[#D1D5DB]'
      }`}
    >
      {/* Top Bar: Provider Identity, Badges & Trust Status */}
      <div className="flex flex-wrap items-center justify-between gap-2 pb-4 border-b border-[#F3F4F6]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#1A1A1A] text-white flex items-center justify-center font-black text-sm shadow-xs">
            {quote.providerLogoText || quote.providerName.substring(0, 2).toUpperCase()}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-[#1A1A1A] text-base">{quote.providerName}</span>
              {isBest && (
                <span className="text-[10px] font-extrabold uppercase bg-[#0E7044] text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Sparkles className="w-2.5 h-2.5" />
                  <span>Best Recipient Amount</span>
                </span>
              )}
              {quote.isPromotional && (
                <span className="text-[10px] font-bold bg-[#FEF3C7] text-[#92400E] px-2 py-0.5 rounded-full">
                  Promo Rate
                </span>
              )}
            </div>
            <div className="text-xs text-[#6B7280] flex items-center gap-2">
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]"></span>
                <span>{quote.quoteType === 'LIVE_VERIFIED_QUOTE' ? 'Live verified quote' : quote.quoteType.replace(/_/g, ' ')}</span>
              </span>
              <span>•</span>
              <span>Payout: {quote.payoutMethodLabel}</span>
            </div>
          </div>
        </div>

        {/* Speed ETA Pill */}
        <div className="flex items-center gap-1.5 text-xs font-semibold text-[#4B5563] bg-[#F9FAFB] border border-[#E5E7EB] px-3 py-1.5 rounded-xl">
          <Clock className="w-3.5 h-3.5 text-[#0E7044]" />
          <span>{quote.deliveryEstimateText}</span>
        </div>
      </div>

      {/* Main Grid Metrics */}
      <div className="py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
        {/* Metric 1: HERO METRIC - Recipient Gets */}
        <div className="space-y-0.5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[#6B7280]">
            Recipient Gets
          </div>
          <div className="text-2xl sm:text-3xl font-black text-[#1A1A1A] tracking-tight">
            {financialFormat(quote.recipientAmount, quote.receiveCurrency)}{' '}
            <span className="text-sm font-bold text-[#0E7044]">{quote.receiveCurrency}</span>
          </div>
          {quote.derivedMetrics.recipientDifferenceVsBest < 0 && (
            <div className="text-[11px] font-semibold text-[#DC2626]">
              {financialFormat(quote.derivedMetrics.recipientDifferenceVsBest, quote.receiveCurrency)} {quote.receiveCurrency} vs best
            </div>
          )}
        </div>

        {/* Metric 2: Transfer Fee */}
        <div className="space-y-0.5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[#6B7280]">
            Transfer Fee
          </div>
          <div className="text-base font-bold text-[#1A1A1A]">
            {quote.feeAmount === 0 ? (
              <span className="text-[#0E7044] bg-[#DCFCE7] px-2 py-0.5 rounded-md text-xs font-extrabold">
                $0.00 Fee
              </span>
            ) : (
              <span>{financialFormat(quote.feeAmount, quote.feeCurrency)} {quote.feeCurrency}</span>
            )}
          </div>
          <div className="text-[11px] text-[#6B7280]">
            Pay via {quote.paymentMethodLabel.split(' ')[0]}
          </div>
        </div>

        {/* Metric 3: Exchange Rate & FX Markup */}
        <div className="space-y-0.5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[#6B7280] flex items-center gap-1">
            <span>Exchange Rate</span>
            <Info className="w-3 h-3 text-[#9CA3AF]" />
          </div>
          <div className="text-sm font-bold font-mono text-[#1A1A1A]">
            1 {quote.sendCurrency} = {formatRateDisplay(quote.providerExchangeRate)} {quote.receiveCurrency}
          </div>
          <div className="text-[11px] font-medium">
            {quote.derivedMetrics.fxMarkupPercentage === 0 ? (
              <span className="text-[#0E7044] font-bold">0.00% markup (Mid-Market)</span>
            ) : (
              <span className="text-[#6B7280]">
                Markup: <strong className="text-[#D97706]">{quote.derivedMetrics.fxMarkupPercentage.toFixed(2)}%</strong>
              </span>
            )}
          </div>
        </div>

        {/* Metric 4: Total Cost & Action CTA */}
        <div className="flex flex-col sm:items-end justify-center space-y-2">
          <div className="text-right hidden lg:block">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#6B7280]">
              Est. Total Cost
            </div>
            <div className="text-sm font-bold text-[#1A1A1A]">
              {financialFormat(quote.derivedMetrics.estimatedTotalCost, quote.sendCurrency)} {quote.sendCurrency}
            </div>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={() => onOpenDetails(quote)}
              className="py-2.5 px-3 text-xs font-bold text-[#1A1A1A] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-xl transition-colors cursor-pointer"
            >
              Details
            </button>

            <a
              href={quote.deepLinkUrl}
              target="_blank"
              rel="sponsored nofollow noopener noreferrer"
              onClick={() => transferEngine.recordOutboundClick(quote.providerId, `${quote.sendingCountry}_${quote.receivingCountry}`)}
              className="flex-1 sm:flex-initial py-2.5 px-4 bg-[#0E7044] hover:bg-[#0B5936] text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-colors shadow-xs cursor-pointer"
            >
              <span>Go to {quote.providerName}</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
